// Motor de bitácora UCI (Fase 4, Tanda 3, ver PLAN.md "Decisión de
// arquitectura" → 113/114/115) — `TablaHoraria24h`: fila-de-parámetro × 24
// columnas horarias (8→7, mismo turno de 24h que usan los 3 formularios
// impresos), parametrizada por catálogo (nombre de fila + unidad) en vez de
// repetida a mano por cada grupo (constantes vitales, laboratorio,
// ventilación, ingresos/egresos...). Todavía SIN conectar a ningún
// formulario real — eso llega en las tandas 5+ (113/114/115), cuando cada
// una aporte su propio catálogo de filas (que vivirá en
// `packages/hce-pdf/src/`, mismo patrón que `denverII.ts`/`odontograma.ts`).
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { fueraDeRango, type RangoEscala } from '../calculosClinicosDatos';

/** Turno de 24h de los 3 formularios impresos: arranca a las 8:00 del día del ingreso y cierra a las 7:00 del día siguiente. */
export const COLUMNAS_HORARIAS_UCI = [
  '08', '09', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '00', '01', '02', '03', '04', '05', '06', '07',
] as const;
export type ColumnaHorariaUci = (typeof COLUMNAS_HORARIAS_UCI)[number];

export interface FilaTablaHoraria24h {
  readonly clave: string;
  readonly etiqueta: string;
  readonly unidad?: string;
  /** Rango entero válido de la fila (p. ej. Glasgow ocular 1-4): fuera de él la celda se marca en rojo, pero NO se bloquea el guardado; una celda vacía nunca se marca. */
  readonly rango?: RangoEscala;
}

export type EstadoTablaHoraria24h = Readonly<Record<string, Readonly<Record<ColumnaHorariaUci, string>>>>;

function filaVacia(): Record<ColumnaHorariaUci, string> {
  return Object.fromEntries(COLUMNAS_HORARIAS_UCI.map((c) => [c, ''])) as Record<ColumnaHorariaUci, string>;
}

/** Estado inicial — una fila vacía (24 columnas) por cada parámetro del catálogo. */
export function estadoInicialTablaHoraria24h(filas: readonly FilaTablaHoraria24h[]): EstadoTablaHoraria24h {
  return Object.fromEntries(filas.map((f) => [f.clave, filaVacia()]));
}

/** Actualiza una sola celda (fila×hora) — pura, exportada para test, mismo criterio que `alternarPuntoPartograma`. */
export function actualizarCeldaTablaHoraria24h(estado: EstadoTablaHoraria24h, filaClave: string, columna: ColumnaHorariaUci, valor: string): EstadoTablaHoraria24h {
  return { ...estado, [filaClave]: { ...(estado[filaClave] ?? filaVacia()), [columna]: valor } };
}

/** Filas del catálogo con al menos una celda cargada — para cuando cada formulario real arme su `Par[]`/diagrama de respaldo (lista dispersa, mismo criterio que `DatosOdontogramaPdf`). */
export function filasConDatoTablaHoraria24h(filas: readonly FilaTablaHoraria24h[], estado: EstadoTablaHoraria24h): readonly FilaTablaHoraria24h[] {
  return filas.filter((f) => Object.values(estado[f.clave] ?? {}).some((v) => v.trim() !== ''));
}

/**
 * Un par hora-valor del `Par[]`, sin ambigüedad: "10 h: 5" (nunca "10:5", que se lee como las 10:05). La hora es la columna del turno de 24 h
 * ("08"…"07"); "h" la marca como hora y no como parte del valor.
 */
export function horaValorTexto(columna: string, valor: string): string {
  return `${columna} h: ${valor}`;
}

/** Resumen compacto "10 h: valor · 11 h: valor" de una fila, para cuando el formulario real no tiene un diagrama propio y arma su `Par[]` de respaldo con texto (mismo criterio de respaldo textual que el resto de los formularios discretos). Cadena vacía si la fila no tiene ningún dato. */
export function resumenFilaTablaHoraria24h(estado: EstadoTablaHoraria24h, filaClave: string): string {
  return COLUMNAS_HORARIAS_UCI.filter((c) => (estado[filaClave]?.[c] ?? '').trim() !== '')
    .map((c) => horaValorTexto(c, estado[filaClave][c]))
    .join(' · ');
}

export interface TablaHoraria24hProps {
  readonly filas: readonly FilaTablaHoraria24h[];
  readonly estado: EstadoTablaHoraria24h;
  readonly onChange: (estado: EstadoTablaHoraria24h) => void;
}

/** Grilla parámetro×24h genérica — un solo componente reusado por los 5-8 grupos de cada uno de 113/114/115 (constantes, laboratorio, ventilación, ingresos/egresos...), en vez de repetirla a mano por grupo. */
export function TablaHoraria24h({ filas, estado, onChange }: TablaHoraria24hProps): JSX.Element {
  const { t } = useTranslation();

  function actualizarCelda(filaClave: string, columna: ColumnaHorariaUci, valor: string): void {
    onChange(actualizarCeldaTablaHoraria24h(estado, filaClave, columna, valor));
  }

  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="border-collapse text-xs">
        <thead>
          <tr>
            <th className="sticky left-0 z-10 bg-hce-surface p-1 text-left font-normal text-hce-muted">
              <span className="sr-only">{t('formularios.uci.tablaHoraria24h.columnaParametro')}</span>
            </th>
            {COLUMNAS_HORARIAS_UCI.map((c) => (
              <th key={c} className="p-1 text-center font-medium text-hce-ink">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filas.map((fila) => (
            <tr key={fila.clave} className="border-t border-border">
              <td className="sticky left-0 z-10 bg-hce-surface whitespace-nowrap p-1 pr-2 font-medium text-hce-ink">
                {fila.etiqueta}
                {fila.unidad ? ` (${fila.unidad})` : ''}
              </td>
              {COLUMNAS_HORARIAS_UCI.map((c) => (
                <td key={c} className="p-0.5">
                  <Input
                    className="h-7 w-14 text-xs"
                    value={estado[fila.clave]?.[c] ?? ''}
                    aria-invalid={fila.rango !== undefined && fueraDeRango(estado[fila.clave]?.[c], fila.rango) ? true : undefined}
                    title={fila.rango !== undefined && fueraDeRango(estado[fila.clave]?.[c], fila.rango) ? `${fila.rango.min}–${fila.rango.max}` : undefined}
                    onChange={(e) => actualizarCelda(fila.clave, c, e.target.value)}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
